import { MenuItemWithIcon } from "@msys/ui";
import { Button, Menu, SvgIcon } from "@mui/material";
import React from "react";
import { useLanguages } from "../../../common/translations/useLanguages.js";
import { ONBOARDING_COLORS } from "../../features/organisations/onboarding/onboardingDesign.js";

const MENU_ID = "signup-language-menu";

/**
 * Language switch of the signup screen: an outlined button naming the current
 * language, opening the app's regular language list. It only restyles the
 * trigger of `commons/LanguageSelector`; switching goes through the same
 * `useLanguages` logic.
 */
export const SignupLanguageSelector = () => {
  const { availableLanguages, selectLanguage, currentLanguage } =
    useLanguages();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        variant="outlined"
        color="inherit"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={isOpen ? MENU_ID : undefined}
        onClick={event => setAnchorEl(event.currentTarget)}
        endIcon={<ChevronDownIcon />}
        sx={{
          height: { xs: 44, tablet: 32 },
          minWidth: 0,
          flexShrink: 0,
          py: 0,
          pl: "10px",
          pr: "10px",
          borderRadius: "6px",
          borderColor: ONBOARDING_COLORS.selectorBorder,
          color: ONBOARDING_COLORS.body,
          fontSize: 13,
          fontWeight: 400,
          lineHeight: 1.45,
          letterSpacing: "normal",
          textTransform: "none",
          "&:hover": {
            borderColor: ONBOARDING_COLORS.selectorBorderHover,
            bgcolor: "transparent",
          },
          "& .MuiButton-endIcon": { ml: "6px", mr: 0 },
        }}
      >
        {currentLanguage.language_name}
      </Button>
      <Menu
        open={isOpen}
        anchorEl={anchorEl}
        id={MENU_ID}
        onClose={handleClose}
      >
        {availableLanguages.map(language => (
          <MenuItemWithIcon
            selected={currentLanguage.locale === language.locale}
            icon={<SvgIcon component={language.flag} viewBox="0 0 512 512" />}
            key={language.locale}
            onClick={() => {
              selectLanguage(language);
              handleClose();
            }}
            style={{ height: 48 }}
          >
            {language.language_name}
          </MenuItemWithIcon>
        ))}
      </Menu>
    </>
  );
};

const ChevronDownIcon = () => (
  <SvgIcon aria-hidden="true" sx={{ width: 20, height: 20 }}>
    <path
      d="m6 9 6 6 6-6"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </SvgIcon>
);
